import queryString from 'query-string'
import { FetchArgs } from '@reduxjs/toolkit/query'
import i18n from 'i18next'
import {
  GetFilterAdvertiserLandingApiResponse,
  GetFilterAdvertiserNamesApiResponse,
  GetFilterCityApiArg,
  GetFilterCityApiResponse,
  GetFilterCountryApiResponse,
  GetFilterEngineApiResponse,
  GetFilterProjectsApiResponse,
} from './endpoints'
import { IOption } from '../../shared/components/forms/search-input/BpSearchInput'
import { ALL_CITIES_VALUE } from '../../shared/consts/AllCities.const'

export type UtmCookies = {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  referer?: string
  last_path?: string
}

export type ProjectOption = IOption & {
  is_demo: boolean
  has_paid_links: boolean
  has_seo_links: boolean
  has_shopping_ads: boolean
  has_trademarks: boolean
  has_active_trademarks: boolean
  has_tm_infringements: boolean
}

export type CountryOption = IOption & {
  keywords_state?: 'active' | 'deleted' | 'never'
  code?: string
}

export const downloadFile = async (response: Response) =>
  window.location.assign(window.URL.createObjectURL(await response.blob()))

export const openFile = async (response: Response) =>
  window.open(window.URL.createObjectURL(await response.blob()))

export const joinIds = <T extends { ids?: number[] }>({ ids, ...rest }: T) => ({
  ...rest,
  ids: ids?.join(','),
})

export const urlencoded = (body: Record<string, unknown>) => {
  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(body)) {
    if (value === undefined) continue

    if (Array.isArray(value)) value.forEach((item) => params.append(`${key}[]`, `${item}`))
    else params.append(key, `${value}`)
  }

  return params
}

export const packUtm = (source: UtmCookies) =>
  JSON.stringify({
    source: source.utm_source,
    medium: source.utm_medium,
    campaign: source.utm_campaign,
    term: source.utm_term,
    content: source.utm_content,
    referer: source.referer?.slice(0, 500),
    last_path: source.last_path?.slice(0, 500),
  })

export const chartBody = (
  body: Record<string, unknown>,
  commaLists: string[],
  json: string[],
  phpArrays: string[] = [],
) => {
  const formData = new FormData()

  for (const [key, value] of Object.entries(body)) {
    if (value === undefined) continue

    if (commaLists.includes(key) && Array.isArray(value)) {
      if (value.length) formData.append(key, value.join(','))
    } else if (phpArrays.includes(key) && Array.isArray(value)) {
      value.forEach((item) => formData.append(`${key}[]`, `${item}`))
    } else if (json.includes(key)) formData.append(key, JSON.stringify(value))
    else formData.append(key, `${value}`)
  }

  return formData
}

export const withExport =
  <A extends { export?: string; exportColumns?: string[] }>(query: (arg: A) => FetchArgs) =>
  (queryArg: A): FetchArgs => {
    const request = query(queryArg)

    if (!queryArg.export) return request

    return {
      ...request,
      params: {
        ...request.params,
        exportColumns: undefined,
        'exportColumns[]': (queryArg as A & { 'exportColumns[]'?: string[] })['exportColumns[]'],
      },
      responseHandler: downloadFile,
      cache: 'no-cache',
    }
  }

export const noCache =
  <A>(query: (arg: A) => FetchArgs) =>
  (queryArg: A): FetchArgs => ({ ...query(queryArg), cache: 'no-cache' })

export const withParams =
  (...keys: string[]) =>
  <A>(query: (arg: A) => FetchArgs) =>
  (queryArg: A): FetchArgs => {
    const request = query(queryArg)
    const extra = queryArg as A & Record<string, unknown>

    return {
      ...request,
      params: { ...request.params, ...Object.fromEntries(keys.map((key) => [key, extra[key]])) },
    }
  }

export const withSort = withParams('sort')

export const withSearch = withParams('search')

export const queryStringQuery =
  <A extends { export?: string }>(query: (arg: A) => FetchArgs) =>
  (queryArg: A): FetchArgs => {
    const { url, params, ...rest } = query(queryArg)
    const brackets = Object.fromEntries(
      Object.entries(queryArg).filter(([key, value]) => key.endsWith('[]') && value !== undefined),
    )
    const replaced = Object.fromEntries(
      Object.keys(brackets).map((key) => [key.slice(0, -2), undefined]),
    )

    return {
      ...rest,
      url: `${url}?${queryString.stringify({
        ...params,
        country_ids: undefined,
        exportColumns: undefined,
        ...replaced,
        ...brackets,
      })}`,
      ...(queryArg.export ? { responseHandler: downloadFile, cache: 'no-cache' as const } : {}),
    }
  }

export const dictToOptions = (response: { data?: { [key: string]: string } }): IOption[] =>
  Object.entries(response.data ?? {}).map(([value, text]) => ({ text, value }))

export const namesToOptions = (response: { data?: { name?: string }[] }): IOption[] =>
  (response.data ?? []).map(({ name }) => ({ text: name as string, value: name as string }))

export const stringsToOptions = (response: { data?: string[] }): IOption[] =>
  (response.data ?? []).map((name) => ({ text: name, value: name }))

export const toProjectOptions = (response: GetFilterProjectsApiResponse): ProjectOption[] =>
  (response.data ?? [])
    .map((o) => ({
      text: o.name,
      value: `${o.id}`,
      is_demo: o.is_demo,
      has_paid_links: o.has_paid_links,
      has_seo_links: o.has_seo_links,
      has_shopping_ads: o.has_shopping_ads,
      has_trademarks: o.has_trademarks,
      has_active_trademarks: o.has_active_trademarks,
      has_tm_infringements: o.has_tm_infringements,
    }))
    .sort((a, b) => Number(a.is_demo) - Number(b.is_demo))

export const toCountryOptions = (response: GetFilterCountryApiResponse): CountryOption[] =>
  (response.data ?? []).map((o) => ({
    text: o.name,
    value: `${o.id}`,
    keywords_state: o.keywords_state,
    code: (o as typeof o & { code?: string }).code,
  }))

export const toEngineOptions = (response: GetFilterEngineApiResponse): IOption[] =>
  (response.data ?? []).map((o) => ({
    text: o.name,
    value: `${o.id}`,
    allowed_country_ids: o.allowed_country_ids,
  }))

export const toLandingOptions = (response: GetFilterAdvertiserLandingApiResponse): IOption[] =>
  (response.data ?? []).map(({ landing_id, name }) => ({ text: name, value: `${landing_id}` }))

export const toAdvertiserNameOptions = (response: GetFilterAdvertiserNamesApiResponse): IOption[] =>
  (response.data ?? []).map((o) => ({ text: o.name, value: `${o.advertiser_id}` }))

export const toCityOptions = (
  response: GetFilterCityApiResponse,
  _meta: unknown,
  arg: GetFilterCityApiArg,
): IOption[] => {
  if (!response.data) return []

  const cities = response.data.map((o) => ({ text: o.name, value: `${o.id}` }))

  if (arg?.all)
    return [{ text: i18n.t('fields.allCities'), value: ALL_CITIES_VALUE, pinned: true }, ...cities]

  return cities
}

export interface IChartModel {
  name: string
  type: string
  data: number[][]
}

export const toLineSeries = (response: { data?: object[] }): IChartModel[] =>
  (response.data?.map((item) => ({ ...item, type: 'line' })) as IChartModel[]) ?? []
